/**
 * useRoleStatus — canonical per-role status for a Kloutforce user.
 *
 * Used by /connector, /agent, /general-agent landing entry points,
 * by RoleApprovedGuard, and by Account → Professional Roles to
 * surface pending / rejected / approved / none state.
 *
 * Sources of truth (read-only, no writes):
 *   - public.agent_profiles (program_type ∈ connector|agent|general_agent)
 *       status in ('approved','active') ⇒ approved
 *   - public.role_applications (requested_role ∈ connector|agent|general_agent)
 *       most recent row drives pending|rejected when no approved profile exists
 *   - public.members for display_name + primary_handle (pending card chrome)
 *
 * Does NOT mutate any approval/activation pipeline.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@bright-web-skeleton/auth';

export type NetworkRole = 'connector' | 'agent' | 'general_agent';

export type RoleStatus =
  | 'loading'
  | 'logged-out'
  | 'none'
  | 'pending'
  | 'rejected'
  | 'approved';

const PENDING_STATUSES = new Set(['pending', 'under_review', 'submitted']);
const REJECTED_STATUSES = new Set(['rejected', 'denied', 'withdrawn']);

export interface RoleStatusInfo {
  status: RoleStatus;
  submittedAt: string | null;
  displayName: string | null;
  primaryHandle: string | null;
}

const EMPTY: RoleStatusInfo = {
  status: 'loading',
  submittedAt: null,
  displayName: null,
  primaryHandle: null,
};

export function useRoleStatus(role: NetworkRole) {
  const { authUser, authReady } = useAuth();
  const [info, setInfo] = useState<RoleStatusInfo>(EMPTY);

  useEffect(() => {
    if (!authReady) return;
    if (!authUser?.id) {
      setInfo({ ...EMPTY, status: 'logged-out' });
      return;
    }

    let cancelled = false;

    (async () => {
      const [profileRes, appRes, memberRes] = await Promise.all([
        supabase
          .from('agent_profiles')
          .select('status')
          .eq('user_id', authUser.id)
          .eq('program_type', role)
          .in('status', ['approved', 'active'])
          .maybeSingle(),
        supabase
          .from('role_applications')
          .select('status, created_at')
          .eq('user_id', authUser.id)
          .eq('requested_role', role)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('members')
          .select('display_name, primary_handle')
          .eq('user_id', authUser.id)
          .maybeSingle(),
      ]);

      if (cancelled) return;

      const profile = profileRes.data;
      const application = appRes.data;
      const member = memberRes.data;
      const baseChrome = {
        displayName: (member?.display_name as string | null) ?? null,
        primaryHandle: (member?.primary_handle as string | null) ?? null,
      };

      if (profile) {
        setInfo({
          status: 'approved',
          submittedAt: (application?.created_at as string | null) ?? null,
          ...baseChrome,
        });
        return;
      }

      const appStatus = (application?.status as string | null)?.toLowerCase() ?? null;
      if (appStatus && PENDING_STATUSES.has(appStatus)) {
        setInfo({
          status: 'pending',
          submittedAt: (application?.created_at as string | null) ?? null,
          ...baseChrome,
        });
        return;
      }
      if (appStatus && REJECTED_STATUSES.has(appStatus)) {
        setInfo({
          status: 'rejected',
          submittedAt: (application?.created_at as string | null) ?? null,
          ...baseChrome,
        });
        return;
      }

      setInfo({ status: 'none', submittedAt: null, ...baseChrome });
    })();

    return () => { cancelled = true; };
  }, [authReady, authUser?.id, role]);

  return info;
}
